import React from "react";
import {Utility} from "@renta-apps/athenaeum-toolkit";

/**
 * Hour fields of a {@link UserSalaryHour} that are summed in the Daily hours totals rows.
 */
export type PageTotalAccessor =
    "nonBillableHours" |
    "normalHours" |
    "absenceHours" |
    "overtime50Hours" |
    "overtime100Hours" |
    "overtimeTotalHours" |
    "grandTotalHours";

export type PageTotalValues = Record<PageTotalAccessor, number>;

export const pageTotalAccessors: readonly PageTotalAccessor[] = [
    "nonBillableHours",
    "normalHours",
    "absenceHours",
    "overtime50Hours",
    "overtime100Hours",
    "overtimeTotalHours",
    "grandTotalHours",
];

export function isPageTotalAccessor(accessor: string | null): accessor is PageTotalAccessor {
    return (accessor != null) && (pageTotalAccessors as readonly string[]).includes(accessor);
}

/**
 * Column layout of the totals table, measured from the rendered header of the Daily hours grid.
 * One entry per visible leaf column of the grid, in the order they are shown.
 */
export interface ITotalsLayout {
    /** Distance of the grid's first column from the left edge of the scroll container content, in px. */
    offsetLeft: number;

    /** Width of each visible leaf column, in px. */
    widths: number[];

    /** Accessor of each visible leaf column (null for action columns). */
    accessors: (string | null)[];
}

/**
 * Index of the first summed column. The label cell spans every column before it (Date, Mounters, Customer and site, Task).
 */
export function getLabelColSpan(layout: ITotalsLayout): number {
    const index: number = layout.accessors.findIndex(accessor => isPageTotalAccessor(accessor));
    return (index > 0) ? index : 1;
}

/**
 * Cells of a totals row after the label cell: a value under each summed column and an empty cell under every other column.
 */
export function renderTotalsValueCells(layout: ITotalsLayout, values: Partial<PageTotalValues>, idPrefix: string, valueClassName: string): React.ReactNode[] {
    const labelColSpan: number = getLabelColSpan(layout);

    return layout.accessors
        .slice(labelColSpan)
        .map((accessor, index) => (isPageTotalAccessor(accessor))
            ? (
                <td key={accessor}
                    id={`${idPrefix}${accessor}`}
                    className={valueClassName}
                >
                    {Utility.formatValue(values[accessor] || 0, "0.0")}
                </td>
            )
            : (
                <td key={`empty_${labelColSpan + index}`}/>
            ));
}
